import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSession } from '../store/session'
import { fullName, roleLabel, fmtMoney, fmtDateTime, titleCase } from '../lib/format'
import { getSummary, listTransactionsOut, type Summary, type TransactionOut } from '../api/monitoring'
import { StatusChip } from '../components/StatusChip'

const COLS = 'minmax(160px,1fr) 150px 120px 130px 140px'

export function Dashboard() {
  const me = useSession((s) => s.me)
  const role = me?.roles[0] ?? ''
  const [sum, setSum] = useState<Summary | null>(null)
  const [recent, setRecent] = useState<TransactionOut[] | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    getSummary()
      .then(setSum)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Could not load summary'))
    listTransactionsOut(8, 0)
      .then((p) => setRecent(p.items))
      .catch(() => setRecent([]))
  }, [])

  const kpis = [
    { label: 'Collected in', val: sum ? fmtMoney(sum.inflow_total) : '—', sub: sum ? `${sum.inflow_count} inflows` : '', dot: 'var(--primary)' },
    { label: 'Disbursed (delivered)', val: sum ? fmtMoney(sum.outflow_total) : '—', sub: sum ? `${sum.delivered} delivered` : '', dot: 'var(--primary)' },
    { label: 'Payouts pending', val: sum ? String(sum.pending) : '—', sub: 'received + submitted', dot: 'var(--primary)' },
    { label: 'Payouts failed', val: sum ? String(sum.failed) : '—', sub: sum ? `${sum.reversed} reversed` : '', dot: 'var(--primary)' },
    { label: 'Stuck payouts', val: sum ? String(sum.stuck) : '—', sub: 'overdue — needs attention', dot: sum && sum.stuck > 0 ? 'var(--danger)' : 'var(--muted)' },
  ]

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Welcome, {me ? fullName(me.firstname, me.lastname, me.email) : ''}</h1>
          <div className="desc">
            Signed in as <b>{roleLabel(role)}</b> · monitor and configure the payment rails.
          </div>
        </div>
      </div>

      {err && <div className="auth-error mt8">{err}</div>}

      {sum && sum.events_stuck > 0 && (
        <div className="auth-error mt8" role="alert">
          ⚠ Event relay stuck: {sum.events_stuck} confirmation{sum.events_stuck === 1 ? '' : 's'} not delivered to the ledger — the message broker may be down.
        </div>
      )}

      <div className="grid-stats">
        {kpis.map((k) => (
          <div className="stat" key={k.label}>
            <div className="lbl">
              <span className="tdot" style={{ background: k.dot }} /> {k.label}
            </div>
            <div className="val num">{k.val}</div>
            <div className="sub">{k.sub || ' '}</div>
          </div>
        ))}
      </div>

      <div className="card pad mt16">
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Recent payouts</span>
          <Link className="btn sm ghost" to="/transactions">
            View all
          </Link>
        </div>
        <div className="tbl tbl-scroll mt8">
          <div className="tr head" style={{ gridTemplateColumns: COLS }}>
            <div>Beneficiary</div>
            <div>Amount</div>
            <div>Status</div>
            <div>Reference</div>
            <div>When</div>
          </div>
          {recent === null &&
            [0, 1, 2].map((i) => (
              <div className="tr body" style={{ gridTemplateColumns: COLS }} key={i}>
                <div className="skeleton" style={{ width: '70%' }} />
                <div className="skeleton" style={{ width: '50%' }} />
                <div className="skeleton" style={{ width: '60%' }} />
                <div className="skeleton" style={{ width: '55%' }} />
                <div className="skeleton" style={{ width: '50%' }} />
              </div>
            ))}
          {recent?.map((t) => (
            <div className="tr body" style={{ gridTemplateColumns: COLS }} key={t.id}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.beneficiary || '—'}</div>
              <div className="num">{fmtMoney(t.amount)}</div>
              <div><StatusChip status={titleCase(t.status)} /></div>
              <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{t.reference}</div>
              <div className="muted" style={{ fontSize: 12 }}>{fmtDateTime(t.created_at)}</div>
            </div>
          ))}
          {recent?.length === 0 && <div className="empty">No payouts yet</div>}
        </div>
      </div>
    </div>
  )
}
